import React, { useEffect, useState } from "react";
import { useFirebase } from "../contexts/FirebaseContext";
import {
  isMissionFilled,
  MISSION_TYPES,
  MISSION_TYPE_OPTIONS,
} from "../constants/missions";

const emptyDraft = () => ({
  title: "",
  text: "",
  imageUrl: "",
  type: MISSION_TYPES.MANUAL_CONTENT,
  rewardPoints: 0,
  config: { target: 1 },
});

const draftFromMission = (mission) => ({
  title: mission.title || "",
  text: mission.text || "",
  imageUrl: mission.imageUrl || "",
  type: mission.type || MISSION_TYPES.MANUAL_CONTENT,
  rewardPoints: mission.rewardPoints || 0,
  config: { target: mission.config?.target || 1 },
});

const AdminMissionsEditor = () => {
  const { getAllMissions, updateMission, uploadMissionImage } = useFirebase();
  const [missions, setMissions] = useState([]);
  const [drafts, setDrafts] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [savingId, setSavingId] = useState(null);
  const [uploadingId, setUploadingId] = useState(null);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [message, setMessage] = useState("");

  useEffect(() => {
    let cancelled = false;

    const load = async () => {
      setLoading(true);
      setError("");
      try {
        const allMissions = await getAllMissions();
        if (cancelled) return;
        setMissions(allMissions);
        const nextDrafts = {};
        allMissions.forEach((mission) => {
          nextDrafts[mission.id] = draftFromMission(mission);
        });
        setDrafts(nextDrafts);
      } catch (err) {
        console.error(err);
        if (!cancelled) {
          setError("Could not load missions. Check Firebase permissions.");
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    load();

    const handleMissionsChanged = () => {
      load();
    };
    window.addEventListener("arcade-missions-changed", handleMissionsChanged);

    return () => {
      cancelled = true;
      window.removeEventListener(
        "arcade-missions-changed",
        handleMissionsChanged
      );
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const updateDraft = (missionId, field, value) => {
    setDrafts((current) => ({
      ...current,
      [missionId]: {
        ...current[missionId],
        [field]: value,
      },
    }));
    setMessage("");
  };

  const updateDraftTarget = (missionId, target) => {
    setDrafts((current) => ({
      ...current,
      [missionId]: {
        ...current[missionId],
        config: { target: Number(target) || 1 },
      },
    }));
    setMessage("");
  };

  const normalizeDraft = (draft) => ({
    title: (draft.title || "").trim(),
    text: (draft.text || "").trim(),
    imageUrl: (draft.imageUrl || "").trim(),
    type: draft.type || MISSION_TYPES.MANUAL_CONTENT,
    rewardPoints: Number(draft.rewardPoints) || 0,
    config: { target: Number(draft.config?.target) || 1 },
    active: true,
  });

  const handleSave = async (missionId) => {
    const draft = drafts[missionId];
    if (!draft) return;

    const payload = normalizeDraft(draft);

    setSavingId(missionId);
    setError("");
    setMessage("");
    try {
      await updateMission(missionId, payload);
      setDrafts((current) => ({
        ...current,
        [missionId]: {
          ...payload,
          config: { target: payload.config.target },
        },
      }));
      setMissions((current) =>
        current.map((mission) =>
          mission.id === missionId
            ? { ...mission, ...payload, updatedAt: new Date() }
            : mission
        )
      );
      setMessage(`Saved mission ${missionId.replace("mission_", "")}.`);
      window.dispatchEvent(new CustomEvent("arcade-missions-changed"));
    } catch (err) {
      console.error(err);
      setError("Could not save mission. Try again.");
    } finally {
      setSavingId(null);
    }
  };

  const handleClear = async (missionId) => {
    const confirmed = window.confirm("Are you sure?");
    if (!confirmed) return;

    const cleared = emptyDraft();
    setDrafts((current) => ({ ...current, [missionId]: cleared }));
    setSavingId(missionId);
    setError("");
    setMessage("");
    try {
      await updateMission(missionId, cleared);
      setMissions((current) =>
        current.map((mission) =>
          mission.id === missionId
            ? { ...mission, ...cleared, updatedAt: new Date() }
            : mission
        )
      );
      setMessage(
        `Cleared mission ${missionId.replace("mission_", "")} — title, text, picture, and rules removed.`
      );
      window.dispatchEvent(new CustomEvent("arcade-missions-changed"));
    } catch (err) {
      console.error(err);
      setError("Could not clear mission. Try again.");
    } finally {
      setSavingId(null);
    }
  };

  const handleImageFile = async (missionId, file) => {
    if (!file) return;
    setUploadingId(missionId);
    setUploadProgress(0);
    setError("");
    setMessage("");
    try {
      const imageUrl = await uploadMissionImage(
        missionId,
        file,
        (percent) => setUploadProgress(percent)
      );
      updateDraft(missionId, "imageUrl", imageUrl);
      setUploadProgress(100);
      setMessage("Picture ready — click Save to keep it.");
    } catch (err) {
      console.error(err);
      setError(err.message || "Could not upload picture. Try again.");
    } finally {
      setUploadingId(null);
      setUploadProgress(0);
    }
  };

  const filledCount = missions.filter((mission) =>
    isMissionFilled(drafts[mission.id] || mission)
  ).length;

  return (
    <section id="admin-missions" className="admin-table-card">
      <div className="admin-table-header">
        <h2>Missions</h2>
        {loading ? (
          <span className="admin-muted">Loading…</span>
        ) : (
          <span className="admin-muted">
            {filledCount} filled / {missions.length} slots
          </span>
        )}
      </div>

      <p className="admin-missions-help">
        Edit content plus rules: type, mega-star reward, and target (streak days
        / hangman wins / games). Save to publish.
      </p>

      {error && <p className="admin-error">{error}</p>}
      {message && <p className="admin-success">{message}</p>}

      {!loading && (
        <div className="admin-missions-grid">
          {missions.map((mission) => {
            const draft = drafts[mission.id] || emptyDraft();
            const filled = isMissionFilled(draft);
            const busy =
              savingId === mission.id || uploadingId === mission.id;
            const needsTarget =
              draft.type === MISSION_TYPES.DAILY_PLAY_STREAK ||
              draft.type === MISSION_TYPES.HANGMAN_WIN_STREAK ||
              draft.type === MISSION_TYPES.PLAY_ALL_GAMES;

            return (
              <article
                key={mission.id}
                id={mission.slot === 1 ? "admin-mission-first" : undefined}
                className={`admin-mission-card ${
                  filled ? "is-filled" : "is-empty"
                }`}
              >
                <div className="admin-mission-card-top">
                  <span className="admin-mission-slot">
                    Slot {String(mission.slot).padStart(2, "0")}
                  </span>
                  <span
                    className={`admin-mission-badge ${
                      filled ? "is-filled" : "is-empty"
                    }`}
                  >
                    {filled ? "Filled" : "Empty"}
                  </span>
                </div>

                <div className="admin-mission-preview">
                  {draft.imageUrl ? (
                    <img src={draft.imageUrl} alt="" />
                  ) : (
                    <div className="admin-mission-preview-empty">No picture</div>
                  )}
                </div>

                <label className="admin-label" htmlFor={`${mission.id}-title`}>
                  Title
                </label>
                <input
                  id={`${mission.id}-title`}
                  className="admin-input"
                  type="text"
                  value={draft.title}
                  onChange={(e) =>
                    updateDraft(mission.id, "title", e.target.value)
                  }
                  disabled={busy}
                  placeholder="Mission title"
                />

                <label className="admin-label" htmlFor={`${mission.id}-text`}>
                  Text
                </label>
                <textarea
                  id={`${mission.id}-text`}
                  className="admin-textarea"
                  rows={3}
                  value={draft.text}
                  onChange={(e) =>
                    updateDraft(mission.id, "text", e.target.value)
                  }
                  disabled={busy}
                  placeholder="Mission description"
                />

                <label className="admin-label" htmlFor={`${mission.id}-type`}>
                  Type
                </label>
                <select
                  id={`${mission.id}-type`}
                  className="admin-input"
                  value={draft.type || MISSION_TYPES.MANUAL_CONTENT}
                  onChange={(e) =>
                    updateDraft(mission.id, "type", e.target.value)
                  }
                  disabled={busy}
                >
                  {MISSION_TYPE_OPTIONS.map((option) => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </select>

                <div className="admin-mission-rules-row">
                  <div>
                    <label
                      className="admin-label"
                      htmlFor={`${mission.id}-reward`}
                    >
                      Reward stars
                    </label>
                    <input
                      id={`${mission.id}-reward`}
                      className="admin-input"
                      type="number"
                      min={0}
                      step={1}
                      value={draft.rewardPoints ?? 0}
                      onChange={(e) =>
                        updateDraft(
                          mission.id,
                          "rewardPoints",
                          e.target.value === "" ? 0 : Number(e.target.value)
                        )
                      }
                      disabled={busy}
                    />
                  </div>
                  {needsTarget && (
                    <div>
                      <label
                        className="admin-label"
                        htmlFor={`${mission.id}-target`}
                      >
                        Target
                      </label>
                      <input
                        id={`${mission.id}-target`}
                        className="admin-input"
                        type="number"
                        min={1}
                        step={1}
                        value={draft.config?.target ?? 1}
                        onChange={(e) =>
                          updateDraftTarget(mission.id, e.target.value)
                        }
                        disabled={busy}
                      />
                    </div>
                  )}
                </div>

                <label className="admin-label" htmlFor={`${mission.id}-image`}>
                  Picture URL
                </label>
                <input
                  id={`${mission.id}-image`}
                  className="admin-input"
                  type="url"
                  value={draft.imageUrl.startsWith("data:") ? "" : draft.imageUrl}
                  onChange={(e) =>
                    updateDraft(mission.id, "imageUrl", e.target.value)
                  }
                  disabled={busy}
                  placeholder="https://… or upload a file below"
                />

                <label className="admin-file-label">
                  <input
                    type="file"
                    accept="image/*"
                    disabled={busy}
                    onChange={(e) => {
                      const file = e.target.files?.[0];
                      handleImageFile(mission.id, file);
                      e.target.value = "";
                    }}
                  />
                  {uploadingId === mission.id
                    ? `Uploading… ${uploadProgress}%`
                    : "Upload picture"}
                </label>
                {uploadingId === mission.id && (
                  <div
                    className="admin-upload-progress"
                    role="progressbar"
                    aria-valuemin={0}
                    aria-valuemax={100}
                    aria-valuenow={uploadProgress}
                    aria-label="Upload progress"
                  >
                    <div
                      className="admin-upload-progress-bar"
                      style={{ width: `${uploadProgress}%` }}
                    />
                    <span className="admin-upload-progress-label">
                      {uploadProgress}%
                    </span>
                  </div>
                )}

                <div className="admin-mission-actions">
                  <button
                    type="button"
                    className="admin-button admin-button--ghost"
                    disabled={busy}
                    onClick={() => handleClear(mission.id)}
                  >
                    Clear
                  </button>
                  <button
                    type="button"
                    className="admin-button"
                    disabled={busy}
                    onClick={() => handleSave(mission.id)}
                  >
                    {savingId === mission.id ? "Saving…" : "Save"}
                  </button>
                </div>
              </article>
            );
          })}
        </div>
      )}
    </section>
  );
};

export default AdminMissionsEditor;
